import React, { useEffect, useState } from 'react';
import { Modal, Typography, Divider, Card, Space, Collapse, Row, Col, Button, Alert, Tabs, Tag, InputNumber, Statistic } from 'antd';
import { useAppContext } from '../libs/contextLib';
import useAllVehicleTypes from '../hooks/useAllVehicleType';
import useAllSchedules from '../hooks/useAllSchedules';
import { ReloadOutlined, DollarOutlined, CarOutlined, ScheduleOutlined, InfoCircleOutlined, UserOutlined, ClockCircleOutlined, EnvironmentOutlined, PieChartOutlined, FileExcelOutlined } from '@ant-design/icons';
import { defaultRates } from '../libs/eaPayments';
import { ReactComponent as Warning } from '../assets/icons/Warning.svg';
import { charterModelData } from '../services/ModelService';
import dayjs from '../dayjs';
import { last } from 'lodash';
import moment from 'moment';
import * as XLSX from 'xlsx';
import useAllCharterSchedules from '../hooks/useAllCharterSchedules';
import { parseSecondsToDuration } from '../libs/date';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const modalStyles = {
  card: {
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    borderRadius: '8px',
    marginBottom: '16px'
  },
  statistic: {
    '& .ant-statistic-title': {
      fontSize: '14px',
      color: 'rgba(0,0,0,0.65)'
    },
    '& .ant-statistic-content': {
      fontSize: '20px',
      fontWeight: '500'
    }
  },
  rateTable: {
    'th, td': {
      padding: '12px 8px',
      fontSize: '14px'
    },
    th: {
      backgroundColor: '#fafafa',
      fontWeight: '500'
    }
  },
  tabContent: {
    padding: '16px 0'
  },
  legPanel: {
    header: {
      fontSize: '15px',
      padding: '12px 16px',
      backgroundColor: '#fafafa',
      borderRadius: '6px'
    },
    content: {
      padding: '16px'
    },
    warning: {
      color: '#ff4d4f',
      fontWeight: 500
    },
    stats: {
      display: 'flex',
      gap: '24px',
      padding: '12px 16px',
      backgroundColor: '#f5f5f5',
      borderRadius: '6px',
      marginBottom: '16px'
    },
    costBreakdown: {
      backgroundColor: '#ffffff',
      padding: '12px',
      borderRadius: '6px',
      border: '1px solid #f0f0f0'
    }
  },
  marginInput: {
    width: '100%'
  }
};

const QuoteDetailsModal = ({ visible, onClose, charter, setBookingWith, editMode = false }) => {
  const { operator } = useAppContext();
  const { allVehicleTypes } = useAllVehicleTypes();
  const { allSchedules } = useAllSchedules();
  const { allCharterSchedules } = useAllCharterSchedules();
  const [activeTab, setActiveTab] = useState('1');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [customMargin, setCustomMargin] = useState(30); // Default 30% margin

  if (!charter) return null;

  // Find the auto-generated quote line item
  const autoGeneratedQuote = charter?.quoteLineItems?.find(item => item?.type === 'auto-generated');
  const quoteDetails = autoGeneratedQuote?.details || {};

  // Get the rates from the charter quote details
  const baseRate = charter?.quoteLineItems?.[0]?.details?.rates?.baseRate || 50;
  const rates = charter?.quoteLineItems?.[0]?.details?.rates || defaultRates;

  // Define penalty rates table
  const penaltyRates = rates?.penaltyRates;

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';

    // Ensure we're working with a valid number
    seconds = Number(seconds) || 0;

    const { days, hours, minutes } = parseSecondsToDuration(seconds);

    // Handle case where minutes round up to 60
    if (minutes === 60) {
      hours += 1;
      minutes = 0;
    }

    let result = '';
    if (days > 0) {
      result += `${days}d `;
    }
    result += `${hours}h ${minutes}m`;

    return result;
  };

  const roundDistance = (meters) => {
    return Math.round((meters / 1000) * 100) / 100;
  };

  const formatDistance = (meters) => {
    if (!meters) return 'N/A';
    return `${roundDistance(meters)}km`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const getVehicleTypeName = (id) => {
    const vehicleType = allVehicleTypes[id];
    return vehicleType ? vehicleType.name : 'Unknown Vehicle';
  };

  const renderLegBreakdown = (leg, index) => {
    // Get quote details from the charter
    const legQuoteDetails = charter?.quoteLineItems?.[0]?.details || {};

    // If we have leg details from the quote, use them directly
    if (legQuoteDetails?.legDetails?.find?.(l => l?.routeId === leg?.routeId)) {
      const legDetails = legQuoteDetails?.legDetails?.find(l => l?.routeId === leg?.routeId) || {};
      const {
        routeName,
        distance: legDistance = 0,
        deadrunDistance = 0,
        startDepotToFirstStop = 0,
        lastStopToEndDepot = 0,
        vehicleCost = 0,
        legDistanceCost: totalDistanceCost = 0,
        legDeadrunCost: deadrunCost = 0,
        vehicleCount = 0,
        driverCostByDate: legDriverCostByDate = {},
        driverCostByDayType: legDriverCostByDayType = {},
        totalDriverCost = 0,
        totalVehicleCost = 0,
        numberOfDates = 0,
        hasMultipleTrips = false
      } = legDetails;

      const totalDistance = legDistance + deadrunDistance;

      // Get per-date costs for this leg based on charter type
      const legDateCosts = charter?.charterType === 'standard' && legDriverCostByDate ?
        Object.entries(legDriverCostByDate)
          .map(([dateKey, dateCost]) => ({
            numberOfVehicles: dateCost?.numberOfVehicles || 0,
            date: dateCost?.date,
            dayType: dateCost?.dayType,
            totalDriverCost: dateCost?.totalDriverCost || 0,
            trips: Object.entries(dateCost?.trips || {}).map(([tripId, tripCost]) => ({
              tripId,
              ...tripCost
            }))
          })) : [];

      // Get day type costs for recurring charters
      const legDayTypeCosts = charter?.charterType === 'recurring' && legDriverCostByDayType ?
        Object.entries(legDriverCostByDayType)
          .filter(([_, typeInfo]) => typeInfo?.count > 0)
          .map(([dayType, typeInfo]) => ({
            numberOfVehicles: typeInfo?.numberOfVehicles || 0,
            dayType,
            count: typeInfo?.count || 0,
            totalCost: typeInfo?.totalCost || 0,
            details: typeInfo?.details || [],
            numberOfDates: typeInfo?.numberOfDates || 0,
            numberOfTrips: typeInfo?.numberOfTrips || 0
          })) : [];

      const allTripsNoVehicles = leg?.services?.every(trip => !trip?.vehicleTypes?.length);
      const someTripsNoVehicles = leg?.services?.some(trip => !trip?.vehicleTypes?.length);

      return (
        <Panel
          key={index}
          header={
            <Row justify="space-between" align="middle" style={modalStyles.legPanel.header}>
              <Col>
                <Space>
                  <CarOutlined style={{ fontSize: '16px' }} />
                  <Text strong>{routeName}</Text>
                  {allTripsNoVehicles && (
                    <Tag color="error">No Vehicles Assigned</Tag>
                  )}
                  {!allTripsNoVehicles && someTripsNoVehicles && (
                    <Tag color="warning">Some Vehicles Not Assigned</Tag>
                  )}
                  {legDetails.numberOfTrips > 1 && (
                    <Tag color="blue">{legDetails.numberOfTrips} Trips</Tag>
                  )}
                </Space>
              </Col>
              <Col>
                <Space size={16}>
                  <Text type="secondary">
                    <ClockCircleOutlined /> {formatDuration(legDetails.duration)}
                  </Text>
                  <Text type="secondary">
                    <EnvironmentOutlined /> {(roundDistance(legDetails.distance) + roundDistance(legDetails.deadrunDistance || 0)).toFixed(2)}km
                  </Text>
                  <Text strong>
                    <DollarOutlined /> {formatCurrency(legDetails.totalVehicleCost + legDetails.totalDriverCost)}
                  </Text>
                </Space>
              </Col>
            </Row>
          }
        >
          <div style={modalStyles.legPanel.content}>
            {/* Quick Stats */}
            <div style={modalStyles.legPanel.stats}>
              <Statistic
                title="Vehicles Required"
                value={`${vehicleCount}`}
                prefix={<CarOutlined />}
                style={modalStyles.statistic}
              />
              <Statistic
                title="Service Distance"
                value={`${formatDistance(legDetails.perTripDistance)}${legDetails.numberOfTrips > 1 ? ` × ${legDetails.numberOfTrips}` : ''}`}
                style={modalStyles.statistic}
              />
              <Statistic
                title="Dead Run Distance"
                value={`${formatDistance(legDetails.perTripDeadrun)}${legDetails.numberOfTrips > 1 ? ` × ${legDetails.numberOfTrips}` : ''}`}
                style={modalStyles.statistic}
              />
            </div>

            {/* Vehicle Costs */}
            <Card
              size="small"
              title={<Space><CarOutlined /> Vehicle Costs</Space>}
              style={{ marginBottom: '16px' }}
            >
              <Row gutter={[16, 8]}>
                <Col span={24}>
                  {legDetails?.tripCosts?.map?.((tripCost, tripIndex) => (
                    <div key={tripCost.tripId || tripIndex} style={modalStyles.legPanel.costBreakdown}>
                      <Row justify="space-between" align="middle" style={{ marginBottom: '12px' }}>
                        <Col>
                          <Text strong>Trip {tripIndex + 1}</Text>
                          {!tripCost.hasVehicles && (
                            <Tag color="error" style={{ marginLeft: '8px' }}>No Vehicle Assigned</Tag>
                          )}
                        </Col>
                        <Col>
                          <Text strong>{formatCurrency(tripCost.totalCost)}</Text>
                        </Col>
                      </Row>

                      {/* Vehicle Breakdown */}
                      {tripCost.vehicleBreakdown.map((item, idx) => (
                        <Row key={idx} justify="space-between" align="middle" style={{ marginBottom: '8px' }}>
                          <Col>{item.name}:</Col>
                          <Col>
                            <Text>{formatCurrency(item.rate)} × {item.quantity} = {formatCurrency(item.total)}</Text>
                          </Col>
                        </Row>
                      ))}

                      <Divider style={{ margin: '12px 0' }} />
                      {/* Distance Costs */}
                      <Row justify="space-between" align="middle" style={{ marginTop: '8px' }}>
                        <Col>Service Distance Cost:</Col>
                        <Col>
                          <Text>{formatDistance(legDetails.perTripDistance)} × {formatCurrency(tripCost.vehicleCost)} x {legDetails.numberOfDates} = {formatCurrency(tripCost.distanceCost * legDetails.numberOfDates)}</Text>
                        </Col>
                      </Row>
                      {legDetails.perTripDeadrun > 0 && (
                        <Row justify="space-between" align="middle" style={{ marginTop: '8px' }}>
                          <Col>Dead Run Cost:</Col>
                          <Col>
                            <Text>{formatDistance(legDetails.perTripDeadrun)} × {formatCurrency(tripCost.vehicleCost)} x {legDetails.numberOfDates} = {formatCurrency(tripCost.deadrunCost * legDetails.numberOfDates)}</Text>
                          </Col>
                        </Row>
                      )}

                      {tripIndex < legDetails.tripCosts.length - 1 && (
                        <Divider style={{ margin: '16px 0' }} />
                      )}
                    </div>
                  ))}
                </Col>
              </Row>
            </Card>

            {/* Driver Costs */}
            <Card
              size="small"
              title={<Space><UserOutlined /> Driver Costs</Space>}
              style={{ marginBottom: '16px' }}
            >
              {/* Standard Charter Date Breakdown */}
              {charter?.charterType === 'standard' && legDateCosts.length > 0 && (
                <Collapse
                  ghost
                  defaultActiveKey={['0']}
                  style={{ background: 'white' }}
                >
                  {legDateCosts.map((dateCost, idx) => (
                    <Panel
                      key={idx}
                      header={
                        <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                          <Col>
                            <Text strong>{typeof dateCost?.date === 'string' ? dateCost?.date : dateCost?.date?.format?.('DD/MM/YYYY') || 'N/A'} ({dateCost?.dayType})</Text>
                          </Col>
                          <Col>
                            <Text strong>{formatCurrency(dateCost?.totalDriverCost)}</Text>
                          </Col>
                        </Row>
                      }
                    >
                      <div style={modalStyles.legPanel.costBreakdown}>
                        {dateCost.trips.map((trip, tripIdx) => (
                          <div key={trip.tripId} style={{ marginBottom: tripIdx < dateCost.trips.length - 1 ? '16px' : 0 }}>
                            {dateCost.trips.length > 1 && (
                              <Row style={{ marginBottom: '8px' }}>
                                <Col>
                                  <Text strong>Trip {tripIdx + 1} - {formatCurrency(trip?.driverCost)}</Text>
                                </Col>
                              </Row>
                            )}
                            <Row gutter={[0, 8]}>
                              <Col span={24}>
                                <Text>Regular Hours ({trip?.regularHours}h):</Text>
                                <div style={{ marginLeft: 16 }}>
                                  <Text type="secondary">
                                    Rate: ${baseRate}/h × {penaltyRates[dateCost?.dayType]?.regularHours} (penalty) × {(1 + rates?.casualLoading)} (casual loading) = ${trip?.regularHourlyRate}/h
                                  </Text>
                                  <br />
                                  <Text type="secondary">
                                    Pay: {trip?.regularHours}h × ${trip?.regularHourlyRate}/h × {dateCost?.numberOfVehicles || 1} (drivers) = {formatCurrency(trip?.regularPayWithLoadingByNumberOfVehicles)}
                                  </Text>
                                </div>
                              </Col>
                              {trip?.tier1OTHours > 0 && (
                                <Col span={24}>
                                  <Text>Tier 1 Overtime ({trip?.tier1OTHours}h):</Text>
                                  <div style={{ marginLeft: 16 }}>
                                    <Text type="secondary">
                                      Rate: ${baseRate}/h × {penaltyRates[dateCost?.dayType]?.tier1OT} (multiplier) × {(1 + rates?.casualLoading)} (casual loading) = ${trip?.tier1OTHourlyRate}/h
                                    </Text>
                                    <br />
                                    <Text type="secondary">
                                      Pay: {trip?.tier1OTHours}h × ${trip?.tier1OTHourlyRate}/h × {dateCost?.numberOfVehicles || 1} (vehicles) = {formatCurrency(trip?.tier1OTPayWithLoadingByNumberOfVehicles)}
                                    </Text>
                                  </div>
                                </Col>
                              )}
                              {trip?.tier2OTHours > 0 && (
                                <Col span={24}>
                                  <Text>Tier 2 Overtime ({trip?.tier2OTHours}h):</Text>
                                  <div style={{ marginLeft: 16 }}>
                                    <Text type="secondary">
                                      Rate: ${baseRate}/h × {penaltyRates[dateCost?.dayType]?.tier2OT} (multiplier) × {(1 + rates?.casualLoading)} (casual loading) = ${trip?.tier2OTHourlyRate}/h
                                    </Text>
                                    <br />
                                    <Text type="secondary">
                                      Pay: {trip?.tier2OTHours}h × ${trip?.tier2OTHourlyRate}/h × {dateCost?.numberOfVehicles || 1} (vehicles) = {formatCurrency(trip?.tier2OTPayWithLoadingByNumberOfVehicles)}
                                    </Text>
                                  </div>
                                </Col>
                              )}
                            </Row>
                            {tripIdx < dateCost.trips.length - 1 && <Divider style={{ margin: '16px 0' }} />}
                          </div>
                        ))}
                      </div>
                    </Panel>
                  ))}
                </Collapse>
              )}

              {/* Recurring Charter Day Type Breakdown */}
              {/* NOTE for Cursor AI: Don't update the visiable text for users from (drivers) to (vehicles). Even thought he calculation is using veicles to calculate costings, the text should still be (drivers) */}
              {charter?.charterType === 'recurring' && legDayTypeCosts.length > 0 && (
                <Collapse
                  ghost
                  defaultActiveKey={['0']}
                  style={{ background: 'white' }}
                >
                  {legDayTypeCosts.map((typeCost, idx) => {
                    const costDetails = typeCost.details[0];
                    return (
                      <Panel
                        key={idx}
                        header={
                          <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                            <Col>
                              <Text strong>{typeCost.dayType} ({typeCost.numberOfDates} days x {typeCost.numberOfTrips} trips = {typeCost.count})</Text>
                            </Col>
                            <Col>
                              <Text strong type="success">{formatCurrency(typeCost.totalCost)}</Text>
                            </Col>
                          </Row>
                        }
                      >
                        <div style={modalStyles.legPanel.costBreakdown}>
                          <Row gutter={[0, 8]}>
                            <Col span={24}>
                              <Text>Regular Hours ({costDetails?.regularHours}h):</Text>
                              <div style={{ marginLeft: 16 }}>
                                <Text type="secondary">
                                  Rate: ${baseRate}/h × {penaltyRates[typeCost?.dayType]?.regularHours} (multiplier) × {(1 + rates?.casualLoading)} (casual loading) = ${costDetails?.regularHourlyRate}/h
                                </Text>
                                <br />
                                <Text type="secondary">
                                  Pay: {costDetails?.regularHours}h × ${costDetails?.regularHourlyRate}/h × {typeCost?.numberOfVehicles || 1} (drivers) = {formatCurrency(costDetails?.regularPayWithLoadingByNumberOfVehicles)}
                                </Text>
                                <br />
                                <Text type="secondary">
                                  Total: {formatCurrency(costDetails?.regularPayWithLoadingByNumberOfVehicles)} x {typeCost?.numberOfDates} days x {typeCost?.numberOfTrips} trips = {formatCurrency(typeCost.totalCost)}
                                </Text>
                              </div>
                            </Col>
                            {costDetails?.tier1OTHours > 0 && (
                              <Col span={24}>
                                <Text>Tier 1 Overtime ({costDetails?.tier1OTHours}h):</Text>
                                <div style={{ marginLeft: 16 }}>
                                  <Text type="secondary">
                                    Rate: ${baseRate}/h × {penaltyRates[typeCost?.dayType]?.tier1OT} (multiplier) × {(1 + rates?.casualLoading)} (casual loading) = ${costDetails?.tier1OTHourlyRate}/h
                                  </Text>
                                  <br />
                                  <Text type="secondary">
                                    Pay: {costDetails?.tier1OTHours}h × ${costDetails?.tier1OTHourlyRate}/h × {typeCost?.numberOfVehicles || 1} (drivers) = {formatCurrency(costDetails?.tier1OTPayWithLoadingByNumberOfVehicles)}
                                  </Text>
                                </div>
                              </Col>
                            )}
                            {costDetails?.tier2OTHours > 0 && (
                              <Col span={24}>
                                <Text>Tier 2 Overtime ({costDetails?.tier2OTHours}h):</Text>
                                <div style={{ marginLeft: 16 }}>
                                  <Text type="secondary">
                                    Rate: ${baseRate}/h × {penaltyRates[typeCost?.dayType]?.tier2OT} (multiplier) × {(1 + rates?.casualLoading)} (casual loading) = ${costDetails?.tier2OTHourlyRate}/h
                                  </Text>
                                  <br />
                                  <Text type="secondary">
                                    Pay: {costDetails?.tier2OTHours}h × ${costDetails?.tier2OTHourlyRate}/h × {typeCost?.numberOfVehicles || 1} (drivers) = {formatCurrency(costDetails?.tier2OTPayWithLoadingByNumberOfVehicles)}
                                  </Text>
                                </div>
                              </Col>
                            )}
                          </Row>
                        </div>
                      </Panel>
                    );
                  })}
                </Collapse>
              )}
            </Card>

            {/* Summary */}
            <Card size="small" title={<Space><PieChartOutlined /> Summary</Space>}>
              <div style={modalStyles.legPanel.costBreakdown}>
                <Row gutter={[0, 8]}>
                  <Col span={24}>
                    <Row justify="space-between" align="middle">
                      <Col>Vehicle Cost ({numberOfDates} days):</Col>
                      <Col><Text>{formatCurrency(totalVehicleCost)}</Text></Col>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Row justify="space-between" align="middle">
                      <Col>Driver Cost ({numberOfDates} days):</Col>
                      <Col><Text>{formatCurrency(totalDriverCost)}</Text></Col>
                    </Row>
                  </Col>
                  <Divider style={{ margin: '8px 0' }} />
                  <Col span={24}>
                    <Row justify="space-between" align="middle">
                      <Col><Text strong>Total Cost:</Text></Col>
                      <Col><Text strong>{formatCurrency(totalVehicleCost + totalDriverCost)}</Text></Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            </Card>

            {allTripsNoVehicles && (
              <Alert
                message="Please select a vehicle type for this charter"
                type="error"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        </Panel>
      );
    }

    return (
      <Panel
        key={index}
        header={
          <Row justify="space-between" align="middle" style={modalStyles.legPanel.header}>
            <Col>
              <Space>
                <CarOutlined style={{ fontSize: '16px' }} />
                <Text strong>{leg.routeName || `Leg ${index + 1}`}</Text>
              </Space>
            </Col>
            <Col>
              <Tag color="warning">No Quote Details</Tag>
            </Col>
          </Row>
        }
      >
        <Alert
          message="Quote details not available for this leg"
          description="Please ensure all required information is provided and try regenerating the quote."
          type="warning"
          showIcon
        />
      </Panel>
    );
  };

  const handleRevert = async () => {
    charter.resetQuoteOverrides();
    await charter.generateQuote({
      operator,
      allVehicleTypes,
      allCharterSchedules
    });
    setBookingWith("quoteLineItems", [...charter.quoteLineItems]);
  };

  const handleExport = () => {
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const data = [];
    const quoteDetails = charter?.quoteLineItems?.[0]?.details || {};

    // Header
    data.push(['Charter Quote Details']);
    data.push(['Generated on:', dayjs().format('DD/MM/YYYY HH:mm:ss')]);
    data.push([]);

    // Quick Summary Section
    data.push(['QUICK SUMMARY']);
    data.push(['Total Duration:', formatDuration(quoteDetails.totalDuration)]);
    data.push(['Total Distance:', formatDistance(quoteDetails.totalDistance)]);
    data.push(['Vehicles Required:', quoteDetails.numberOfVehicles]);
    data.push([]);

    // Line Items with Detailed Breakdown
    data.push(['LINE ITEMS']);
    charter.quoteLineItems.forEach((item, index) => {
      data.push([`Line Item ${index + 1}: ${item.name}`]);
      data.push(['Description:', item.description]);
      data.push(['Base Unit Price:', formatCurrency(item.baseUnitPrice)]);
      if (item.details?.manualUnitPrice !== null && item.details?.manualUnitPrice !== undefined) {
        data.push(['Manually set Unit Price:', formatCurrency(item.details.manualUnitPrice)]);
      }
      if (item.margin) {
        data.push(['Margin Amount:', formatCurrency(item.margin)]);
        data.push(['Selling Price:', formatCurrency(item.sellingPrice)]);
      }
      if (item.details?.manualDiscount !== null && item.details?.manualDiscount !== undefined) {
        data.push(['Manually set Discount:', formatCurrency(item.details.manualDiscount)]);
      } else if (item.discount) {
        data.push(['Discount Amount:', formatCurrency(item.discount)]);
      }
      data.push(['Final Unit Price:', formatCurrency(item.unitPrice)]);
      data.push(['Quantity:', item.quantity]);
      if (item.details?.manualQuantity !== null && item.details?.manualQuantity !== undefined) {
        data.push(['Manually set Quantity:', item.details.manualQuantity]);
      }
      data.push(['Total:', formatCurrency(charter.calculateLineItemAmount(item))]);
      data.push([]);
    });

    // Financial Summary
    data.push(['FINANCIAL SUMMARY']);
    data.push(['Subtotal (exc GST):', formatCurrency(charter.calculateSubTotal())]);
    data.push(['GST:', formatCurrency(charter.calculateGST())]);
    data.push(['Total (inc GST):', formatCurrency(charter.calculateTotal())]);
    data.push([]);

    // Driver Cost Information
    data.push(['DRIVER COST INFORMATION']);
    data.push(['Base Rate:', `$${baseRate}/hour`]);
    data.push(['Casual Loading:', `${((rates?.casualLoading || 0.25) * 100)?.toFixed(0)}%`]);
    data.push([]);

    // Penalty Rates Table
    data.push(['PENALTY RATES']);
    data.push(['Day Type', 'Regular Hours (≤10hrs)', 'Tier 1 OT (10-12hrs)', 'Tier 2 OT (>12hrs)']);

    // Access penalty rates correctly from the rates object
    const penaltyRates = rates?.penaltyRates || {};
    Object.entries(penaltyRates).forEach(([dayType, dayRates]) => {
      data.push([
        dayType,
        `${dayRates?.regularHours?.multiplier || 1}x`,
        `${dayRates?.tier1OT?.multiplier || 1.5}x`,
        `${dayRates?.tier2OT?.multiplier || 2}x`
      ]);
    });
    data.push([]);

    // Leg Details
    data.push(['LEG DETAILS']);
    charter.itinerary?.forEach((leg, index) => {
      const legDetails = quoteDetails.legDetails?.[index];
      if (legDetails) {
        data.push([`Leg ${index + 1}: ${leg.routeName}`]);
        data.push(['Distance Details']);
        data.push(['Service Distance:', formatDistance(legDetails.distance)]);
        data.push(['Deadrun Distance:', formatDistance(legDetails.deadrunDistance)]);
        data.push(['Total Distance:', formatDistance(legDetails.distance + legDetails.deadrunDistance)]);
        data.push(['Total Driver Cost:', formatCurrency(legDetails.totalDriverCost)]);
        data.push(['Number of Vehicles:', legDetails.vehicleCount]);
        data.push([]);

        // Vehicle Costs
        data.push(['Vehicle Breakdown']);
        legDetails.tripCosts.forEach((tripCost, tripIndex) => {
          data.push([`Trip ${tripIndex + 1}`]);
          tripCost.vehicleBreakdown.forEach(vehicle => {
            data.push([
              vehicle.name,
              `Rate: ${formatCurrency(vehicle.rate)}`,
              `Quantity: ${vehicle.quantity}`,
              `Total: ${formatCurrency(vehicle.total)}`
            ]);
          });
          data.push(['Distance Cost:', formatCurrency(tripCost.distanceCost)]);
          data.push(['Deadrun Cost:', formatCurrency(tripCost.deadrunCost)]);
          data.push(['Total Cost:', formatCurrency(tripCost.totalCost)]);
          data.push([]);
        });

        // Driver Costs
        data.push(['Driver Cost Breakdown']);
        if (charter.charterType === 'recurring') {
          Object.entries(legDetails.driverCostByDayType).forEach(([dayType, costs]) => {
            if (costs.count > 0) {
              data.push([`${dayType} (${costs.count} days)`]);
              costs.details.forEach(detail => {
                data.push(['Regular Hours:', detail.regularHours, 'Rate:', formatCurrency(detail.regularHourlyRate), 'Total:', formatCurrency(detail.regularPayWithLoadingByNumberOfVehicles)]);
                if (detail.tier1OTHours > 0) {
                  data.push(['Tier 1 OT Hours:', detail.tier1OTHours, 'Rate:', formatCurrency(detail.tier1OTHourlyRate), 'Total:', formatCurrency(detail.tier1OTPayWithLoadingByNumberOfVehicles)]);
                }
                if (detail.tier2OTHours > 0) {
                  data.push(['Tier 2 OT Hours:', detail.tier2OTHours, 'Rate:', formatCurrency(detail.tier2OTHourlyRate), 'Total:', formatCurrency(detail.tier2OTPayWithLoadingByNumberOfVehicles)]);
                }
              });
              data.push([]);
            }
          });
        } else {
          Object.entries(legDetails.driverCostByDate || {}).forEach(([dateKey, dateInfo]) => {
            data.push([dayjs(dateKey).format('DD/MM/YYYY'), `(${dateInfo.dayType})`]);
            Object.values(dateInfo.trips).forEach(trip => {
              data.push(['Regular Hours:', trip.regularHours, 'Rate:', formatCurrency(trip.regularHourlyRate), 'Total:', formatCurrency(trip.regularPayWithLoadingByNumberOfVehicles)]);
              if (trip.tier1OTHours > 0) {
                data.push(['Tier 1 OT Hours:', trip.tier1OTHours, 'Rate:', formatCurrency(trip.tier1OTHourlyRate), 'Total:', formatCurrency(trip.tier1OTPayWithLoadingByNumberOfVehicles)]);
              }
              if (trip.tier2OTHours > 0) {
                data.push(['Tier 2 OT Hours:', trip.tier2OTHours, 'Rate:', formatCurrency(trip.tier2OTHourlyRate), 'Total:', formatCurrency(trip.tier2OTPayWithLoadingByNumberOfVehicles)]);
              }
            });
            data.push([]);
          });
        }
        data.push([]);
      }
    });

    // Create the worksheet
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Add some styling to the worksheet
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = { c: C, r: R };
        const cell_ref = XLSX.utils.encode_cell(cell_address);
        const cell = ws[cell_ref];
        if (!cell || !cell.v) continue;

        // Style headers
        const cellValue = String(cell.v);
        if (cellValue.includes('SUMMARY') ||
          cellValue.includes('LINE ITEMS') ||
          cellValue.includes('DRIVER COST INFORMATION') ||
          cellValue.includes('PENALTY RATES') ||
          cellValue.includes('LEG DETAILS')) {
          cell.s = {
            font: { bold: true, sz: 14 },
            fill: { fgColor: { rgb: "CCCCCC" } }
          };
        }
      }
    }

    // Auto-size columns
    const max_width = data.reduce((w, r) => Math.max(w, r.length), 0);
    ws['!cols'] = Array(max_width).fill({ wch: 20 });

    XLSX.utils.book_append_sheet(wb, ws, 'Charter Quote Details');

    // Save the workbook
    const fileName = `Charter_Quote_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const renderQuickSummary = () => {
    const {
      totalDistance = 0,
      totalDuration = 0,
      hasMissingVehicles = false,
      numberOfVehicles = 0,
      marginInclusionAmount = 0,
      marginInclusion = 0,
    } = quoteDetails;

    const cost = quoteDetails.baseUnitPrice;
    const marginAmount = quoteDetails.marginInclusionAmount;
    const sellingPrice = cost + marginAmount;
    const discount = quoteDetails.discount;
    const sellingPriceAfterDiscount = sellingPrice * (1 - discount / 100);
    const profit = sellingPriceAfterDiscount - cost;
    const profitMarginPercentage = sellingPriceAfterDiscount === 0
      ? 0
      : ((profit / sellingPriceAfterDiscount) * 100).toFixed(2);

    // For UI
    const finalQuoteExGST = sellingPriceAfterDiscount;



    return (
      <Card size="small" title="Quick Summary" style={modalStyles.card}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <table style={{ width: '100%', borderCollapse: 'collapse', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', borderRadius: '4px', overflow: 'hidden' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', width: '33%', color: 'rgba(0,0,0,0.85)', fontWeight: '500', borderLeft: '1px solid #f0f0f0' }}>Total Cost</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', width: '33%', color: 'rgba(0,0,0,0.85)', fontWeight: '500', borderLeft: '1px solid #f0f0f0' }}>+ Profit Margin</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', width: '33%', color: 'rgba(0,0,0,0.85)', fontWeight: '500' }}>= Total Quote <span style={{ fontSize: '10px', color: 'rgba(0,0,0,0.45)', marginLeft: '4px' }}>(exc gst)</span></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '16px', fontSize: '20px', fontWeight: 'bold', borderLeft: '1px solid #f0f0f0' }}>
                    {formatCurrency(cost)}
                  </td>
                  <td style={{ padding: '16px', fontSize: '20px', fontWeight: 'bold', borderLeft: '1px solid #f0f0f0' }}>
                    {formatCurrency(profit)}
                    <span style={{ fontSize: '14px', color: 'rgba(0,0,0,0.45)', marginLeft: '4px' }}>
                      ({profitMarginPercentage}%)
                    </span>
                  </td>
                  <td style={{ padding: '16px', fontSize: '20px', fontWeight: 'bold' }}>
                    {formatCurrency(finalQuoteExGST)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Col>
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#f5f5f5', padding: '12px 16px', borderRadius: '4px' }}>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <Text type="secondary">Duration</Text>
                <div style={{ marginTop: '4px' }}>
                  <Text strong style={{ fontSize: '16px' }}><ClockCircleOutlined style={{ marginRight: '4px' }} />{formatDuration(totalDuration)}</Text>
                </div>
              </div>
              <div style={{ textAlign: 'center', flex: 1, borderLeft: '1px solid #e8e8e8', borderRight: '1px solid #e8e8e8', padding: '0 8px' }}>
                <Text type="secondary">Vehicles Required</Text>
                <div style={{ marginTop: '4px' }}>
                  <Text strong style={{ fontSize: '16px' }}><CarOutlined style={{ marginRight: '4px' }} />{numberOfVehicles}</Text>
                </div>
              </div>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <Text type="secondary">Total Distance</Text>
                <div style={{ marginTop: '4px' }}>
                  <Text strong style={{ fontSize: '16px' }}><EnvironmentOutlined style={{ marginRight: '4px' }} />{formatDistance(quoteDetails.totalDistance)}</Text>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {hasMissingVehicles && (
          <Alert
            message="Warning: Some charters have no vehicle types assigned"
            type="warning"
            showIcon
            style={{ marginTop: '16px' }}
          />
        )}
      </Card>
    );
  };

  const renderDetailedBreakdown = () => {
    // Calculate line item totals
    const lineItemTotals = charter.quoteLineItems.map(item => ({
      ...item,
      total: charter.calculateLineItemAmount(item),
      margin: item.margin || 0,
      discount: item.discount || 0
    }));

    // Check if any manual override is present
    const hasManualOverride = lineItemTotals.some(item =>
      item.details?.manualUnitPrice !== null && item.details?.manualUnitPrice !== undefined ||
      item.details?.manualQuantity !== null && item.details?.manualQuantity !== undefined ||
      item.details?.manualDiscount !== null && item.details?.manualDiscount !== undefined
    );

    // Get all totals
    const subtotal = charter.calculateSubTotal();
    const gst = charter.calculateGST();
    const total = charter.calculateTotal();
    const discount = charter.getDiscount();
    const marginInclusion = charter.getMarginInclusion(operator);

    return (
      <div style={modalStyles.tabContent}>
        {hasManualOverride && (
          <Alert
            type="warning"
            showIcon
            message="Some values in this quote have been manually overridden. The calculations below may not be valid. You can reset to revert to the original calculation."
            style={{ marginBottom: 16 }}
          />
        )}
        {renderQuickSummary()}

        {/* Cost Breakdown */}
        <Card size="small" title="Quote Breakdown" style={modalStyles.card}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {/* Line Items */}
            {lineItemTotals.map((item, index) => (
              <Row key={index} gutter={[8, 8]}>
                <Col span={12}>
                  <Text>{item.name}:</Text>
                  {item.description && (
                    <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                      {item.description}
                    </Text>
                  )}
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                  <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                    Charter Cost Base (× {item.quantity}): {formatCurrency(item.baseUnitPrice)}
                  </Text>
                  {index === 0 ? <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                    Booking Margin ({marginInclusion}%): {formatCurrency(item.margin)}
                  </Text> : null}
                  {index === 0 ? <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                    Charter Sale Price: {formatCurrency(item.sellingPrice)}
                  </Text> : null}
                  {item.details?.manualUnitPrice !== null && item.details?.manualUnitPrice !== undefined && (
                    <Text type="warning" style={{ display: 'block', fontSize: '12px' }}>
                      Manually set Unit Price: {formatCurrency(item.details.manualUnitPrice)}
                    </Text>
                  )}
                  {item.details?.manualQuantity !== null && item.details?.manualQuantity !== undefined && (
                    <Text type="warning" style={{ display: 'block', fontSize: '12px' }}>
                      Manually set Quantity: {item.details.manualQuantity}
                    </Text>
                  )}
                  {item.details?.manualDiscount !== null && item.details?.manualDiscount !== undefined && (
                    <Text type="warning" style={{ display: 'block', fontSize: '12px' }}>
                      Manually set Discount: {formatCurrency(item.details.manualDiscount)}
                    </Text>
                  )}
                  {item.discount ? <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                    Discount ({discount}%): {formatCurrency(item.discount)}
                  </Text> : null}
                  <Text>
                    Subtotal: {formatCurrency(item.total)}
                  </Text>
                </Col>
              </Row>
            ))}

            <Divider style={{ margin: '12px 0' }} />

            {/* {marginInclusion > 0 && (
              <Row gutter={[8, 8]}>
            <Col span={12}>
                  <Text>Booking Margin ({marginInclusion}%):</Text>
            </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                  <Text>{formatCurrency(marginInclusionAmount)}</Text>
            </Col>
          </Row>
            )} */}

            {/* Subtotal */}
            <Row gutter={[8, 8]}>
              <Col span={12}><Text strong>Subtotal:</Text></Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Text strong>{formatCurrency(subtotal)}</Text>
              </Col>
            </Row>

            {/* GST */}
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Text>GST (10%):</Text>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Text>{formatCurrency(gst)}</Text>
              </Col>
            </Row>

            {/* Total */}
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Title level={4} style={{ margin: 0 }}>TOTAL:</Title>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Title level={4} style={{ margin: 0 }}>{formatCurrency(total)}</Title>
              </Col>
            </Row>
          </Space>
        </Card>

        {/* Charter Details */}
        <Card size="small" title="Charter Cost Base" style={modalStyles.card}>
          <Collapse defaultActiveKey={[]} style={{ background: 'transparent' }}>
            {charter.itinerary?.map((leg, index) => renderLegBreakdown(leg, index))}
          </Collapse>
        </Card>
      </div>
    );
  };

  const renderDriverCostsTab = () => (
    <div style={modalStyles.tabContent}>
      <Card size="small" title="Rate Information" style={modalStyles.card}>
        <div style={{ marginLeft: 16 }}>
          <table style={{ ...modalStyles.rateTable, width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Day Type</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Regular Hours<br />(≤10hrs)</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Tier 1 OT<br />(10-12hrs)</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Tier 2 OT<br />(&gt;12hrs)</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px' }}>Weekday</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.weekday?.regularHours?.multiplier || 1.0}x</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.weekday?.tier1OT?.multiplier || 1.5}x</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.weekday?.tier2OT?.multiplier || 2.0}x</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px' }}>Saturday</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.saturday?.regularHours?.multiplier || 1.5}x</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.saturday?.tier1OT?.multiplier || 1.5}x</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.saturday?.tier2OT?.multiplier || 2.0}x</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px' }}>Sunday</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.sunday?.regularHours?.multiplier || 2.0}x</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.sunday?.tier1OT?.multiplier || 2.0}x</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.sunday?.tier2OT?.multiplier || 2.0}x</td>
              </tr>
              <tr>
                <td style={{ padding: '8px' }}>Public Holiday</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.publicHoliday?.regularHours?.multiplier || 2.5}x</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.publicHoliday?.tier1OT?.multiplier || 2.5}x</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{rates?.penaltyRates?.publicHoliday?.tier2OT?.multiplier || 2.5}x</td>
              </tr>
            </tbody>
          </table>

          <Row style={{ marginTop: '16px' }}>
            <Col span={12}>
              <Text>Base Rate:</Text>
            </Col>
            <Col span={12}>
              <Text strong>${baseRate}/hour</Text>
            </Col>
          </Row>
          <Row style={{ marginTop: '8px' }}>
            <Col span={12}>
              <Text>Casual Loading:</Text>
            </Col>
            <Col span={12}>
              <Text strong>{((rates?.casualLoading || 0.25) * 100)?.toFixed(0)}%</Text>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined />
          <span>Quote Calculation Details</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      styles={{
        body: { padding: '16px' }
      }}
      footer={[
        editMode && (
          <Button
            key="revert"
            icon={<ReloadOutlined />}
            onClick={handleRevert}
            style={{ marginLeft: '8px' }}
          >
            Recalculate Quote
          </Button>
        ),
        <Button
          key="export"
          icon={<FileExcelOutlined />}
          onClick={handleExport}
          style={{ float: 'left' }}
        >
          Export Details
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>
      ]}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0 1px' }}>
        <Tabs defaultActiveKey="breakdown">
          <TabPane
            tab={
              <span>
                <InfoCircleOutlined className='mr-2' />
                Detailed Breakdown
              </span>
            }
            key="breakdown"
          >
            {renderDetailedBreakdown()}
          </TabPane>
          <TabPane
            tab={
              <span>
                <InfoCircleOutlined className='mr-2' />
                EA Rules
              </span>
            }
            key="driver"
          >
            {renderDriverCostsTab()}
          </TabPane>
        </Tabs>
      </div>
    </Modal>
  );
};

export default QuoteDetailsModal; 